/**
 * Reading and writing the nodes of a map in schema v2 (map-format.js), for
 * YjsDocManager: one key per field, a node's place in the node only, deleted
 * ids kept. Also the move from v1 (migration), the changes an older client
 * brings late (folded in), and the way back to v1 (rollback).
 * Plan: docs/plans/2026-10-02-crdt-schema-v2.md.
 *
 * Callers wrap writes in a transaction (with its origin); these methods do
 * not open one, so a batch stays one change.
 */
import * as Y from "yjs";
import {
  SCHEMA_V2, NODES_V1, NODES_V2, DELETED_NODES, V2_MIGRATIONS,
  LOCAL_FIELDS, STRUCTURE_FIELDS, readV2Nodes, placeV1, placeV2, treeNodeToFlat,
} from "./map-format.js";
import { keyBetween, keysBetween, isValidOrderKey } from "./order-key.js";

// Fields that place a node; the rest are its content.
const PLACE_FIELDS = new Set(["parentId", "order"]);
// Who edited a node last, and when (written with every edit).
const STAMP_FIELDS = ["_lastEditedBy", "_lastEditedAt"];

/** null/undefined mean "not set"; so does an empty legacy notes value. */
function isSet(field, value) {
  if (value === null || value === undefined) return false;
  if (field === "notes" && (value === "" || (Array.isArray(value) && value.length === 0))) return false;
  return true;
}

function same(a, b) {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== "object" || typeof b !== "object") return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

/** What a v2 node stores of a flat node: not the shape, not the per-device fields, nothing unset. */
export function storedFields(flat) {
  const out = {};
  for (const [field, value] of Object.entries(flat || {})) {
    if (STRUCTURE_FIELDS.has(field) || LOCAL_FIELDS.has(field)) continue;
    if (isSet(field, value)) out[field] = value;
  }
  return out;
}

/** Whether Yjs item `id` was written by migration `m`. */
const writtenBy = (m, id) => m.client === id.client && id.clock >= m.from && id.clock < m.to;
/** Whether migration `m` had seen Yjs item `id` (its state vector covers it). */
const seenBy = (m, id) => (m.base?.[id.client] ?? 0) > id.clock;

export class MapNodesV2 {
  /** @param {import('./yjs-doc.js').YjsDocManager} manager */
  constructor(manager) {
    this.manager = manager;
    this.doc = manager.doc;
    this.map = this.doc.getMap(NODES_V2);
    this.deleted = this.doc.getMap(DELETED_NODES);
    this.metadata = this.doc.getMap("metadata");
    this._cache = null;
    // Any change to the nodes, the deleted ids or the root: read again.
    this.doc.on("afterTransaction", (transaction) => {
      if (this._cache && this.touches(transaction)) this._cache = null;
    });
  }

  /** Whether a transaction changed the v2 nodes (a field, a node, a deletion) or the metadata. */
  touches(transaction) {
    return transaction.changed.has(this.map) || transaction.changedParentTypes.has(this.map)
      || transaction.changed.has(this.deleted) || transaction.changed.has(this.metadata);
  }

  /** The live nodes and where each shows (cached until the next change). */
  state() {
    if (!this._cache) {
      const flat = readV2Nodes(this.doc);
      const rootId = this.metadata.get("rootId") || null;
      this._cache = { flat, rootId, place: placeV2(flat, rootId, this.deleted) };
    }
    return this._cache;
  }

  _changed() { this._cache = null; }

  /** In the map and shown (not deleted, not under a deleted node). */
  has(id) {
    const { flat, place } = this.state();
    return flat.has(id) && !place.hidden.has(id);
  }

  get(id) {
    return this.has(id) ? { ...this.state().flat.get(id) } : null;
  }

  count() {
    const { flat, place } = this.state();
    return flat.size - place.hidden.size;
  }

  forEach(fn) {
    const { flat, place } = this.state();
    for (const [id, node] of flat) if (!place.hidden.has(id)) fn({ ...node }, id);
  }

  _node(id) {
    const value = this.map.get(id);
    return value instanceof Y.Map && !this.deleted.has(id) ? value : null;
  }

  /**
   * Write fields into a node: only what differs, unset values remove the key.
   * replace: also remove the keys `fields` does not have.
   * @returns {number} keys written
   */
  _write(ymap, fields, { replace = false } = {}) {
    let written = 0;
    for (const [field, value] of Object.entries(fields)) {
      if (!isSet(field, value)) {
        if (ymap.has(field)) { ymap.delete(field); written++; }
      } else if (!same(ymap.get(field), value)) {
        ymap.set(field, value);
        written++;
      }
    }
    if (replace) {
      for (const field of [...ymap.keys()]) {
        if (!(field in fields)) { ymap.delete(field); written++; }
      }
    }
    return written;
  }

  /** Create a node (or rewrite the one with that id): `fields` hold everything, place included. */
  _put(id, fields) {
    const existing = this.map.get(id);
    if (existing instanceof Y.Map) {
      this._write(existing, fields, { replace: true });
    } else {
      const ymap = new Y.Map();
      for (const [field, value] of Object.entries(fields)) if (isSet(field, value)) ymap.set(field, value);
      this.map.set(id, ymap);
    }
    // Added again on purpose (moved back from another map, imported again).
    if (this.deleted.has(id)) this.deleted.delete(id);
  }

  /**
   * The order key for a node put at `index` among the children `parentId`
   * shows (without `movingId`), and the siblings to give new keys when there
   * is no room (two concurrent inserts at one place made equal keys).
   */
  orderAt(parentId, index, movingId = null) {
    const { flat, place } = this.state();
    const siblings = (place.children.get(parentId) || []).filter((id) => id !== movingId);
    const at = index >= 0 && index < siblings.length ? index : siblings.length;
    const orderOf = (id) => {
      const order = flat.get(id)?.order;
      return isValidOrderKey(order) ? order : null;
    };
    const before = at > 0 ? orderOf(siblings[at - 1]) : null;
    const after = at < siblings.length ? orderOf(siblings[at]) : null;
    const boundsValid = (at === 0 || before !== null) && (at === siblings.length || after !== null);
    if (boundsValid && (before === null || after === null || before < after)) {
      return { order: keyBetween(before, after), rekey: [] };
    }
    const keys = keysBetween(null, null, siblings.length + 1);
    return {
      order: keys[at],
      rekey: siblings.map((id, i) => [id, keys[i < at ? i : i + 1]]),
    };
  }

  _rekey(rekey) {
    for (const [id, order] of rekey) this._node(id)?.set("order", order);
  }

  /** Per-device fields of a tree node go to this device's state, not the map. */
  _keepLocal(node) {
    const local = {};
    for (const field of LOCAL_FIELDS) if (node[field] !== undefined) local[field] = node[field];
    if (Object.keys(local).length) this.manager.local.set(node.id, local);
  }

  /** Fields to store for a tree node; its notes go to its fragment in yxml-v1 maps. */
  _fieldsOfTreeNode(node, stamp) {
    const flat = { ...treeNodeToFlat(node, null), ...stamp };
    const notes = typeof node.notes === "string" ? node.notes : "";
    if (this.metadata.get("notesFormat") === "yxml-v1") {
      flat.notes = "";
      if (notes.trim()) this.manager.setNodeNotesFromMarkdown(node.id, notes);
    }
    this._keepLocal(node);
    return storedFields(flat);
  }

  /** Add a node with its subtree under `parentId`, at `index` among the children shown. */
  add(node, parentId, index, stamp) {
    const { order, rekey } = this.orderAt(parentId, index);
    this._rekey(rekey);
    const put = (n, pId, orderKey) => {
      this._put(n.id, { ...this._fieldsOfTreeNode(n, stamp), parentId: pId, order: orderKey });
      const children = n.children || [];
      const keys = keysBetween(null, null, children.length);
      children.forEach((child, i) => put(child, n.id, keys[i]));
    };
    put(node, parentId, order);
    this._changed();
  }

  /**
   * Update fields. Per-device fields go to this device's state.
   * @returns {{found: boolean, written: number, local: Object}}
   */
  update(id, updates, stamp) {
    const ymap = this._node(id);
    if (!ymap) return { found: false, written: 0, local: {} };
    const local = {};
    const fields = {};
    for (const [field, value] of Object.entries(updates)) {
      if (LOCAL_FIELDS.has(field)) local[field] = value;
      else if (!STRUCTURE_FIELDS.has(field)) fields[field] = value;
    }
    if (Object.keys(local).length) this.manager.local.set(id, local);
    let written = this._write(ymap, fields);
    if (written && stamp) written += this._write(ymap, stamp);
    if (written) this._changed();
    return { found: true, written, local };
  }

  /** Whether an update would write anything to the map (to skip empty transactions). */
  wouldWrite(id, updates) {
    const ymap = this._node(id);
    if (!ymap) return false;
    return Object.entries(updates).some(([field, value]) =>
      !LOCAL_FIELDS.has(field) && !STRUCTURE_FIELDS.has(field)
      && (isSet(field, value) ? !same(ymap.get(field), value) : ymap.has(field)));
  }

  /** Move a node under `parentId`, at `index` among the children shown there. */
  move(id, parentId, index, stamp) {
    const ymap = this._node(id);
    if (!ymap) return false;
    const { order, rekey } = this.orderAt(parentId, index, id);
    this._rekey(rekey);
    this._write(ymap, { parentId, order, ...stamp });
    this._changed();
    return true;
  }

  /**
   * Delete a node and what shows under it; their ids are kept as deleted,
   * so a node moved or added under them at the same time goes with them.
   * @returns {string[]} the deleted ids
   */
  remove(id) {
    if (!this.has(id)) return [];
    const { place } = this.state();
    const removed = [];
    const walk = (nodeId) => {
      removed.push(nodeId);
      for (const child of place.children.get(nodeId) || []) walk(child);
    };
    walk(id);
    const at = Date.now();
    for (const nodeId of removed) {
      this.map.delete(nodeId);
      this.deleted.set(nodeId, at);
    }
    this._changed();
    return removed;
  }

  /**
   * Bring nodes back to how a version of the map had them (map history,
   * docs/plans/2026-10-21-map-history.md). `target`: id → the version's
   * stored fields, `parentId` (none for the root) and `order` included. Each
   * is written in place, only what differs (a node deleted since comes back
   * with its id, so links to it work again); `stamp` is added to the nodes
   * that changed. removeOthers (a full revert): the nodes the version did not
   * have are deleted, after the others are back in place (a node moved since
   * under a node created since is not deleted with it).
   * @param {Map<string, Object>} target
   * @returns {{written: string[], removed: string[]}}
   */
  restore(target, { removeOthers = false, stamp = {} } = {}) {
    const written = [];
    for (const [id, node] of target) {
      const fields = storedFields(node);
      if (!node.parentId) delete fields.parentId;
      const existing = this._node(id);
      if (existing) {
        // Who edited last is not content: kept as it is, then stamped below
        // when something else changed.
        for (const key of STAMP_FIELDS) {
          if (existing.has(key)) fields[key] = existing.get(key);
          else delete fields[key];
        }
        if (this._write(existing, fields, { replace: true }) > 0) {
          this._write(existing, stamp);
          written.push(id);
        }
      } else {
        this._put(id, { ...fields, ...stamp });
        written.push(id);
      }
    }
    this._changed();
    const removed = [];
    if (removeOthers) {
      const at = Date.now();
      for (const id of [...this.map.keys()]) {
        if (target.has(id) || this.deleted.has(id)) continue;
        this.map.delete(id);
        this.deleted.set(id, at);
        removed.push(id);
      }
      this._changed();
    }
    return { written, removed };
  }

  /**
   * Replace the whole map with a tree (import): nodes with the same id are
   * rewritten in place, the others deleted (kept as deleted ids).
   * created: a new map starting in v2; recorded like a migration that saw
   * no v1 data, so v1 content from another copy (there should be none) is
   * folded in, not ignored (`foldLateV1`).
   */
  replaceTree(root, stamp = {}, { created = false } = {}) {
    const client = this.doc.clientID;
    const from = Y.getState(this.doc.store, client);
    const keep = new Set();
    const put = (n, pId, orderKey) => {
      keep.add(n.id);
      const fields = { ...this._fieldsOfTreeNode(n, stamp), order: orderKey };
      if (pId) fields.parentId = pId;
      this._put(n.id, fields);
      const children = n.children || [];
      const keys = keysBetween(null, null, children.length);
      children.forEach((child, i) => put(child, n.id, keys[i]));
    };
    put(root, null, keyBetween(null, null));
    const at = Date.now();
    for (const id of [...this.map.keys()]) {
      if (keep.has(id)) continue;
      this.map.delete(id);
      this.deleted.set(id, at);
    }
    this.metadata.set("rootId", root.id);
    if (created) {
      const to = Y.getState(this.doc.store, client);
      this.doc.getMap(V2_MIGRATIONS).set(`${client}:${from}`, { client, from, to, base: {}, created: true, at: Date.now() });
    }
    this._changed();
  }

  /**
   * Add the nodes of a tree the map does not have (a consistency repair):
   * after the children their parent shows. Deleted ids stay deleted.
   * @returns {number} nodes added
   */
  mergeAdditive(root, stamp) {
    const { flat, place } = this.state();
    const missing = []; // [{node, parentId}] in tree order
    const visit = (n, parentId) => {
      if (!flat.has(n.id) && !this.deleted.has(n.id) && !(this.map.get(n.id) instanceof Y.Map)) {
        missing.push({ node: n, parentId });
      }
      (n.children || []).forEach((child) => visit(child, n.id));
    };
    visit(root, null);
    const byParent = new Map();
    for (const entry of missing) {
      if (!byParent.has(entry.parentId)) byParent.set(entry.parentId, []);
      byParent.get(entry.parentId).push(entry);
    }
    for (const [parentId, entries] of byParent) {
      const shown = place.children.get(parentId) || [];
      const last = shown.map((id) => flat.get(id)?.order).filter(isValidOrderKey).sort().at(-1) || null;
      const keys = keysBetween(last, null, entries.length);
      entries.forEach(({ node }, i) => {
        const fields = { ...this._fieldsOfTreeNode(node, stamp), order: keys[i] };
        if (parentId) fields.parentId = parentId;
        this._put(node.id, fields);
      });
    }
    if (missing.length) this._changed();
    return missing.length;
  }

  /**
   * Build v2 from the v1 nodes, as v1 shows them (one place per node,
   * orphans under their parent or the root), leaving `nodes` untouched.
   * Records the migration (its state vector and the clock range it wrote)
   * so a v1 change it did not see can be folded in later (`foldLateV1`).
   * Run inside one transaction.
   * @returns {number} nodes migrated
   */
  migrateFromV1() {
    const v1 = this.doc.getMap(NODES_V1);
    const rootId = this.metadata.get("rootId");
    if (!rootId || !v1.has(rootId)) return 0;
    // A map moved back to v1 and migrated again: start from v1 alone.
    for (const id of [...this.map.keys()]) this.map.delete(id);
    for (const id of [...this.deleted.keys()]) this.deleted.delete(id);

    const base = Object.fromEntries(Y.decodeStateVector(Y.encodeStateVector(this.doc)));
    const client = this.doc.clientID;
    const from = Y.getState(this.doc.store, client);
    const { children } = placeV1(v1, rootId);
    let count = 0;
    const put = (id, parentId, orderKey) => {
      const flat = v1.get(id);
      const fields = { ...storedFields(flat), order: orderKey };
      delete fields.parentId;
      if (parentId) fields.parentId = parentId;
      const ymap = new Y.Map();
      for (const [field, value] of Object.entries(fields)) ymap.set(field, value);
      this.map.set(id, ymap);
      count++;
      const kids = children.get(id) || [];
      const keys = keysBetween(null, null, kids.length);
      kids.forEach((child, i) => put(child, id, keys[i]));
    };
    put(rootId, null, keyBetween(null, null));
    const to = Y.getState(this.doc.store, client);
    this.doc.getMap(V2_MIGRATIONS).set(`${client}:${from}`, { client, from, to, base, at: Date.now() });
    this.metadata.set("schemaVersion", SCHEMA_V2);
    this._changed();
    return count;
  }

  /**
   * Fold in the v1 changes no migration saw: made offline on an older
   * release (or on this one before the map moved), and sent after another
   * device migrated the map. Run inside one transaction; idempotent.
   *
   * - A node added in v1: added (deleted ids stay deleted; under a deleted
   *   parent it is deleted with it, decision 3).
   * - A node changed in v1: each field it changed, unless v2 changed that
   *   field since the migration (then both are edits of one field; v2's
   *   stays). A move, if its new parent lists it in v1; moves come after
   *   the added nodes, so a move into a node added in v1 is folded in the
   *   same pass (one pass leaves nothing for the next).
   * - A node deleted in v1: not folded; the node stays (nothing is lost; an
   *   offline deletion on an older release may need doing again).
   * - An order change among siblings in v1: not folded.
   * @returns {number} nodes changed
   */
  foldLateV1(stamp = {}) {
    const migrations = [...this.doc.getMap(V2_MIGRATIONS).values()].filter((m) => m && m.base);
    if (!migrations.length) return 0;
    const v1 = this.doc.getMap(NODES_V1);
    const migrationOf = (id) => migrations.find((m) => writtenBy(m, id));
    const added = []; // [{id, flat}]
    const moved = []; // [{id, ymap, late}]: after the added nodes
    let changed = 0;
    v1._map.forEach((item, id) => {
      if (item.deleted) return;
      const late = v1.get(id);
      if (!late || typeof late !== "object") return;
      const ymap = this.map.get(id);
      const nodeItem = this.map._map.get(id);
      if (ymap instanceof Y.Map && nodeItem && !nodeItem.deleted) {
        const migration = migrationOf(nodeItem.id);
        if (!migration || seenBy(migration, item.id)) return; // made in v2, or the migration read it
        if (this._foldFields(ymap, late, migrationOf)) changed++;
        moved.push({ id, ymap, late });
        return;
      }
      if (this.deleted.has(id)) return; // deleted in v2
      if (migrations.some((m) => seenBy(m, item.id))) return; // was migrated, then deleted in v2
      added.push({ id, flat: late });
    });
    if (added.length) {
      // Each parent's new children in their v1 order, after what it shows.
      const byParent = new Map();
      for (const entry of added) {
        const parentId = entry.flat.parentId || this.metadata.get("rootId");
        if (!byParent.has(parentId)) byParent.set(parentId, []);
        byParent.get(parentId).push(entry);
      }
      const { flat, place } = this.state();
      for (const [parentId, entries] of byParent) {
        const listed = v1.get(parentId)?.childIds || [];
        entries.sort((a, b) => listed.indexOf(a.id) - listed.indexOf(b.id));
        const shown = place.children.get(parentId) || [];
        const last = shown.map((id) => flat.get(id)?.order).filter(isValidOrderKey).sort().at(-1) || null;
        const keys = keysBetween(last, null, entries.length);
        entries.forEach(({ id, flat: late }, i) => {
          this._put(id, { ...storedFields(late), parentId, order: keys[i] });
        });
      }
      changed += added.length;
      this._changed();
    }
    for (const { id, ymap, late } of moved) {
      if (this._foldMove(id, ymap, late, v1, migrationOf, stamp)) changed++;
    }
    if (changed) this._changed();
    return changed;
  }

  /** The content fields a late v1 value changed, where v2 still holds the migrated value. */
  _foldFields(ymap, late, migrationOf) {
    const lateFields = storedFields(late);
    const names = new Set([...Object.keys(late), ...ymap.keys()]);
    let written = 0;
    for (const field of names) {
      if (STRUCTURE_FIELDS.has(field) || LOCAL_FIELDS.has(field) || PLACE_FIELDS.has(field)) continue;
      // Changed in v2 since the migration: set again, or removed (the
      // migration's item is then deleted).
      const fieldItem = ymap._map.get(field);
      if (fieldItem && (fieldItem.deleted || !migrationOf(fieldItem.id))) continue;
      const value = lateFields[field];
      if (value === undefined ? ymap.has(field) : !same(ymap.get(field), value)) {
        if (value === undefined) ymap.delete(field);
        else ymap.set(field, value);
        written++;
      }
    }
    return written > 0;
  }

  /** A late v1 move: to its new parent, at the end, if that parent lists it and v2 did not move it since. */
  _foldMove(id, ymap, late, v1, migrationOf, stamp) {
    const parentId = late.parentId;
    if (!parentId || parentId === ymap.get("parentId")) return false;
    const parentItem = ymap._map.get("parentId");
    if (parentItem && !migrationOf(parentItem.id)) return false; // moved in v2 since
    if (!(v1.get(parentId)?.childIds || []).includes(id)) return false;
    if (!(this.map.get(parentId) instanceof Y.Map) && !this.deleted.has(parentId)) return false;
    const { order } = this.orderAt(parentId, -1, id);
    this._write(ymap, { parentId, order, ...stamp });
    this._changed(); // the next late move into this parent goes after this one
    return true;
  }

  /** When the map moved to v2 (or was created in it): the newest record's time, or null. */
  convertedAt() {
    let at = null;
    for (const m of this.doc.getMap(V2_MIGRATIONS).values()) {
      if (m && typeof m.at === "number" && (at === null || m.at > at)) at = m.at;
    }
    return at;
  }

  /**
   * The old copy's nodes v2 does not account for: not a v2 node, not
   * deleted in v2. Dropping the copy must wait while there are any.
   * @returns {string[]} ids
   */
  oldCopyMissing() {
    const missing = [];
    this.doc.getMap(NODES_V1).forEach((value, id) => {
      if (!value || typeof value !== "object") return; // not a node
      if (this.map.get(id) instanceof Y.Map || this.deleted.has(id)) return;
      missing.push(id);
    });
    return missing;
  }

  /**
   * Delete every entry of the old copy (docs/plans/2026-10-13-drop-schema-v1.md).
   * Run inside one transaction, after `foldLateV1` and `oldCopyMissing`.
   * @returns {number} entries deleted
   */
  dropOldCopy() {
    const v1 = this.doc.getMap(NODES_V1);
    const ids = [...v1.keys()];
    for (const id of ids) v1.delete(id);
    return ids.length;
  }

  /**
   * The way back: write the tree v2 shows into `nodes` (whole v1 nodes with
   * their children lists, this device's folded branches and sizes), delete
   * from `nodes` what v2 deleted (deleted ids, nodes deleted with a parent),
   * and mark the map v1. v1 nodes v2 never had stay (v1 shows them as
   * orphans). `nodesV2` stays.
   * Run inside one transaction, by a client that still knows v2, before an
   * older release goes back out.
   * @returns {number} nodes written
   */
  rollbackToV1() {
    const { flat, rootId, place } = this.state();
    if (!rootId || !flat.has(rootId)) return 0;
    const v1 = this.doc.getMap(NODES_V1);
    const shown = new Set();
    const write = (id, parentId) => {
      shown.add(id);
      const { order: _order, parentId: _parentId, id: _id, ...fields } = flat.get(id);
      const old = v1.get(id) || {};
      const local = this.manager.local.get(id) || {};
      const localFields = {};
      for (const field of LOCAL_FIELDS) {
        const value = local[field] !== undefined ? local[field] : old[field];
        if (value !== undefined) localFields[field] = value;
      }
      const childIds = place.children.get(id) || [];
      v1.set(id, { ...fields, ...localFields, id, parentId, childIds });
      childIds.forEach((child) => write(child, id));
    };
    write(rootId, null);
    for (const id of [...v1.keys()]) {
      if (shown.has(id)) continue;
      if (this.deleted.has(id) || place.hidden.has(id)) v1.delete(id);
    }
    this.metadata.delete("schemaVersion");
    this.metadata.set("v2RolledBackAt", Date.now());
    this._changed();
    return shown.size;
  }
}
